import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config';
import { handlePhotoButtonClick } from '../utils/photoHandler';
import { handleSocioUpdate } from '../utils/socioUpdateHandler';
import './EditarSocio.css';

function EditarSocio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [socio, setSocio] = useState(null);
  const [form, setForm] = useState({});
  const [codigoPais, setCodigoPais] = useState('+598');
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const fileInputRef = useRef();
  const [fotoKey, setFotoKey] = useState(Date.now());

  const [fotoPreview, setFotoPreview] = useState('');
  const [fotoAnterior, setFotoAnterior] = useState(null);
  const [nuevaFoto, setNuevaFoto] = useState(null);

  useEffect(() => {
    const fetchSocio = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_ENDPOINTS.SOCIOS}/${id}`, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        const data = await res.json();
        if (res.ok) {
          setSocio(data.socio);
          setForm(data.socio);
          
          // Configurar preview de foto
          if (data.socio.foto) {
            const { construirUrlFoto } = require('../utils/photoUrlHelper');
            setFotoPreview(construirUrlFoto(data.socio.foto));
            setFotoAnterior(data.socio.foto);
          }
          
          // Extraer código de país del teléfono
          if (data.socio.telefono && data.socio.telefono.match(/^\+\d+/)) {
            const codigo = data.socio.telefono.match(/^\+\d+/)[0];
            setCodigoPais(codigo);
          }
        } else {
          setError(data.message || 'Error al cargar socio');
        }
      } catch (err) {
        setError('Error de red');
      }
    };
    fetchSocio();
  }, [id]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCodigoPaisChange = e => {
    setCodigoPais(e.target.value);
  };

  const handleTelefonoChange = e => {
    setForm({ ...form, telefono: e.target.value });
  };

  const handleFotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNuevaFoto(file);
      setFotoPreview(URL.createObjectURL(file));
      setFotoKey(Date.now());
    }
  };

  // Función para validar CI Uruguay
  function validarCiUruguay(ci) {
    // Eliminar puntos y guiones
    ci = ci.replace(/[.\-]/g, '');
    if (!/^\d{7,8}$/.test(ci)) return false;
    let a = 0;
    let ciArr = ci.split('').map(Number);
    let base = [2,9,8,7,6,3,4];
    while (ciArr.length < 8) ciArr.unshift(0);
    for (let i = 0; i < 7; i++) a += (base[i] * ciArr[i]) % 10;
    let check = (10 - (a % 10)) % 10;
    return check === ciArr[7];
  }

  const validar = () => {
    if (!form.nombreCompleto?.trim()) return 'El nombre completo es obligatorio.';
    if (!form.documento?.trim()) return 'El documento es obligatorio.';
    if (!form.tipoDocumento) return 'El tipo de documento es obligatorio.';
    if (form.tipoDocumento === 'CI Uruguay' && !validarCiUruguay(form.documento)) return 'La cédula de identidad no es válida.';
    if (form.correo && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.correo)) return 'El correo no es válido.';
    if (form.anioUltimaCuota && (isNaN(form.anioUltimaCuota) || form.anioUltimaCuota < 1900)) return 'El año de la última cuota no es válido.';
    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    await handleSocioUpdate({
      form,
      socio,
      codigoPais,
      fotoAnterior,
      nuevaFoto,
      setMensaje,
      setError,
      setLoading,
      setSocio,
      setForm,
      setFotoPreview,
      setFotoAnterior,
      setFotoKey,
      socioId: id,
      validar
    });
  };

  if (!socio) return <div>Cargando...</div>;

  return (
    <div className="panel-socio-bg">
      <div className="panel-socio-card">
        <h2 className="panel-socio-title">✏️ Editar Socio</h2>
        
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '1rem', width: '100%', justifyContent: 'center'}}>
          {fotoPreview && <img key={fotoKey} src={fotoPreview} alt="Foto del socio" className="panel-socio-foto" />}
          <button
            type="button"
            className="btn-panel-socio btn-cambiar-foto"
            style={{marginLeft: 16, padding: '0.4rem 1rem', fontSize: '0.95rem', minWidth: 0, width: 'auto', height: 38}}
            onClick={() => handlePhotoButtonClick(fileInputRef, subiendoFoto)}
            disabled={subiendoFoto}
          >
            {subiendoFoto ? 'Subiendo...' : 'Cambiar foto'}
          </button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{display: 'none'}}
            onChange={handleFotoChange}
          />
        </div>
        
        <form onSubmit={handleSubmit} className="panel-socio-form">
          <div className="form-columns">
            <div className="form-column">
              <label>Número de Socio</label>
              <input name="numeroSocio" value={form.numeroSocio || ''} onChange={handleChange} required />
              
              <label>Nombre Completo *</label>
              <input name="nombreCompleto" value={form.nombreCompleto || ''} onChange={handleChange} required />
              
              <label>Documento *</label>
              <input name="documento" value={form.documento || ''} onChange={handleChange} required />
              
              <label>Tipo de Documento *</label>
              <select name="tipoDocumento" value={form.tipoDocumento || ''} onChange={handleChange} required>
                <option value="">Seleccionar</option>
                <option value="CI Uruguay">CI Uruguay</option>
                <option value="Otro">Otro</option>
              </select>
              
              <label>Fecha de ingreso</label>
              <input name="fechaIngreso" type="date" value={form.fechaIngreso ? form.fechaIngreso.substring(0,10) : ''} onChange={handleChange} />
              
              <label>Categoría</label>
              <select name="categoria" value={form.categoria || ''} onChange={handleChange}>
                <option value="Activo">Activo</option>
                <option value="Colaborador">Colaborador</option>
                <option value="Suscriptor">Suscriptor</option>
                <option value="Dama">Dama</option>
                <option value="Menor">Menor</option>
                <option value="Fallecido">Fallecido</option>
                <option value="Borrado">Borrado</option>
              </select>
              
              <label>Administrador</label>
              <select name="administrador" value={form.administrador ? 'Sí' : 'No'} onChange={handleChange}>
                <option value="Sí">Sí</option>
                <option value="No">No</option>
              </select>
            </div>
            
            <div className="form-column">
              <label>Dirección</label>
              <input name="direccion" value={form.direccion || ''} onChange={handleChange} />
              
              <label>Correo electrónico</label>
              <input name="correo" type="email" value={form.correo || ''} onChange={handleChange} />
              
              <label>Año de la última cuota paga</label>
              <input name="anioUltimaCuota" type="number" value={form.anioUltimaCuota || ''} onChange={handleChange} />
              
              <label>Teléfono</label>
              <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center', width: '100%', justifyContent: 'center'}}>
                <select 
                  value={codigoPais} 
                  onChange={handleCodigoPaisChange}
                  style={{
                    padding: '0.4rem 0.7rem',
                    border: '1px solid #D32F2F',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    minWidth: '140px',
                    maxWidth: '140px',
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                  }}
                >
                  <option value="+598">🇺🇾 Uruguay (+598)</option>
                  <option value="+93">🇦🇫 Afganistán (+93)</option>
                  <option value="+27">🇿🇦 Sudáfrica (+27)</option>
                  <option value="+49">🇩🇪 Alemania (+49)</option>
                  <option value="+213">🇩🇿 Argelia (+213)</option>
                  <option value="+966">🇸🇦 Arabia Saudita (+966)</option>
                  <option value="+54">🇦🇷 Argentina (+54)</option>
                  <option value="+43">🇦🇹 Austria (+43)</option>
                  <option value="+61">🇦🇺 Australia (+61)</option>
                  <option value="+880">🇧🇩 Bangladesh (+880)</option>
                  <option value="+32">🇧🇪 Bélgica (+32)</option>
                  <option value="+591">🇧🇴 Bolivia (+591)</option>
                  <option value="+975">🇧🇹 Bután (+975)</option>
                  <option value="+267">🇧🇼 Botsuana (+267)</option>
                  <option value="+55">🇧🇷 Brasil (+55)</option>
                  <option value="+257">🇧🇮 Burundi (+257)</option>
                  <option value="+855">🇰🇭 Camboya (+855)</option>
                  <option value="+1">🇨🇦 Canadá (+1)</option>
                  <option value="+682">🇨🇰 Islas Cook (+682)</option>
                  <option value="+56">🇨🇱 Chile (+56)</option>
                  <option value="+86">🇨🇳 China (+86)</option>
                  <option value="+57">🇨🇴 Colombia (+57)</option>
                  <option value="+269">🇰🇲 Comoras (+269)</option>
                  <option value="+506">🇨🇷 Costa Rica (+506)</option>
                  <option value="+53">🇨🇺 Cuba (+53)</option>
                  <option value="+850">🇰🇵 Corea del Norte (+850)</option>
                  <option value="+82">🇰🇷 Corea del Sur (+82)</option>
                  <option value="+235">🇹🇩 Chad (+235)</option>
                  <option value="+45">🇩🇰 Dinamarca (+45)</option>
                  <option value="+971">🇦🇪 Emiratos Árabes Unidos (+971)</option>
                  <option value="+593">🇪🇨 Ecuador (+593)</option>
                  <option value="+20">🇪🇬 Egipto (+20)</option>
                  <option value="+34">🇪🇸 España (+34)</option>
                  <option value="+1">🇺🇸 Estados Unidos (+1)</option>
                  <option value="+291">🇪🇷 Eritrea (+291)</option>
                  <option value="+251">🇪🇹 Etiopía (+251)</option>
                  <option value="+358">🇫🇮 Finlandia (+358)</option>
                  <option value="+679">🇫🇯 Fiyi (+679)</option>
                  <option value="+33">🇫🇷 Francia (+33)</option>
                  <option value="+240">🇬🇶 Guinea Ecuatorial (+240)</option>
                  <option value="+233">🇬🇭 Ghana (+233)</option>
                  <option value="+502">🇬🇹 Guatemala (+502)</option>
                  <option value="+245">🇬🇼 Guinea-Bisáu (+245)</option>
                  <option value="+504">🇭🇳 Honduras (+504)</option>
                  <option value="+852">🇭🇰 Hong Kong (+852)</option>
                  <option value="+36">🇭🇺 Hungría (+36)</option>
                  <option value="+91">🇮🇳 India (+91)</option>
                  <option value="+62">🇮🇩 Indonesia (+62)</option>
                  <option value="+353">🇮🇪 Irlanda (+353)</option>
                  <option value="+972">🇮🇱 Israel (+972)</option>
                  <option value="+39">🇮🇹 Italia (+39)</option>
                  <option value="+81">🇯🇵 Japón (+81)</option>
                  <option value="+253">🇩🇯 Yibuti (+253)</option>
                  <option value="+686">🇰🇮 Kiribati (+686)</option>
                  <option value="+254">🇰🇪 Kenia (+254)</option>
                  <option value="+856">🇱🇦 Laos (+856)</option>
                  <option value="+266">🇱🇸 Lesoto (+266)</option>
                  <option value="+94">🇱🇰 Sri Lanka (+94)</option>
                  <option value="+60">🇲🇾 Malasia (+60)</option>
                  <option value="+960">🇲🇻 Maldivas (+960)</option>
                  <option value="+261">🇲🇬 Madagascar (+261)</option>
                  <option value="+692">🇲🇭 Islas Marshall (+692)</option>
                  <option value="+52">🇲🇽 México (+52)</option>
                  <option value="+691">🇫🇲 Micronesia (+691)</option>
                  <option value="+95">🇲🇲 Myanmar (+95)</option>
                  <option value="+230">🇲🇺 Mauricio (+230)</option>
                  <option value="+505">🇳🇮 Nicaragua (+505)</option>
                  <option value="+234">🇳🇬 Nigeria (+234)</option>
                  <option value="+683">🇳🇺 Niue (+683)</option>
                  <option value="+64">🇳🇿 Nueva Zelanda (+64)</option>
                  <option value="+687">🇳🇨 Nueva Caledonia (+687)</option>
                  <option value="+47">🇳🇴 Noruega (+47)</option>
                  <option value="+977">🇳🇵 Nepal (+977)</option>
                  <option value="+31">🇳🇱 Países Bajos (+31)</option>
                  <option value="+507">🇵🇦 Panamá (+507)</option>
                  <option value="+675">🇵🇬 Papúa Nueva Guinea (+675)</option>
                  <option value="+595">🇵🇾 Paraguay (+595)</option>
                  <option value="+51">🇵🇪 Perú (+51)</option>
                  <option value="+63">🇵🇭 Filipinas (+63)</option>
                  <option value="+48">🇵🇱 Polonia (+48)</option>
                  <option value="+689">🇵🇫 Polinesia Francesa (+689)</option>
                  <option value="+351">🇵🇹 Portugal (+351)</option>
                  <option value="+44">🇬🇧 Reino Unido (+44)</option>
                  <option value="+420">🇨🇿 República Checa (+420)</option>
                  <option value="+242">🇨🇬 República del Congo (+242)</option>
                  <option value="+243">🇨🇩 República Democrática del Congo (+243)</option>
                  <option value="+236">🇨🇫 República Centroafricana (+236)</option>
                  <option value="+7">🇷🇺 Rusia (+7)</option>
                  <option value="+250">🇷🇼 Ruanda (+250)</option>
                  <option value="+685">🇼🇸 Samoa (+685)</option>
                  <option value="+239">🇸🇹 Santo Tomé y Príncipe (+239)</option>
                  <option value="+221">🇸🇳 Senegal (+221)</option>
                  <option value="+248">🇸🇨 Seychelles (+248)</option>
                  <option value="+65">🇸🇬 Singapur (+65)</option>
                  <option value="+252">🇸🇴 Somalia (+252)</option>
                  <option value="+503">🇸🇻 El Salvador (+503)</option>
                  <option value="+46">🇸🇪 Suecia (+46)</option>
                  <option value="+41">🇨🇭 Suiza (+41)</option>
                  <option value="+268">🇸🇿 Suazilandia (+268)</option>
                  <option value="+66">🇹🇭 Tailandia (+66)</option>
                  <option value="+886">🇹🇼 Taiwán (+886)</option>
                  <option value="+255">🇹🇿 Tanzania (+255)</option>
                  <option value="+690">🇹🇰 Tokelau (+690)</option>
                  <option value="+216">🇹🇳 Túnez (+216)</option>
                  <option value="+90">🇹🇷 Turquía (+90)</option>
                  <option value="+688">🇹🇻 Tuvalu (+688)</option>
                  <option value="+380">🇺🇦 Ucrania (+380)</option>
                  <option value="+256">🇺🇬 Uganda (+256)</option>
                  <option value="+58">🇻🇪 Venezuela (+58)</option>
                  <option value="+84">🇻🇳 Vietnam (+84)</option>
                  <option value="+681">🇼🇫 Wallis y Futuna (+681)</option>
                  <option value="+967">🇾🇪 Yemen (+967)</option>
                  <option value="+260">🇿🇲 Zambia (+260)</option>
                  <option value="+263">🇿🇼 Zimbabue (+263)</option>
                </select>
                <input 
                  name="telefono" 
                  value={form.telefono ? form.telefono.replace(/^\+\d+\s/, '') : ''} 
                  onChange={handleTelefonoChange}
                  placeholder="Número de teléfono"
                  style={{
                    flex: 1,
                    minWidth: '160px',
                    maxWidth: '160px'
                  }}
                />
              </div>
              
              <label>Fecha de nacimiento</label>
              <input name="fechaNacimiento" type="date" value={form.fechaNacimiento ? form.fechaNacimiento.substring(0,10) : ''} onChange={handleChange} />
              
              <label>Usuario que dio de alta</label>
              <input name="usuarioAlta" value={form.usuarioAlta || ''} readOnly style={{backgroundColor: '#f5f5f5'}} />
            </div>
          </div>
          
          {mensaje && <div className="panel-socio-mensaje">{mensaje}</div>}
          {error && <div className="panel-socio-error">{error}</div>}
        </form>
        
        <div className="panel-socio-botones">
          <button type="button" className="btn-panel-socio btn-accion" onClick={handleSubmit} disabled={loading} style={{marginBottom: 8}}>
            {loading ? 'Guardando...' : '💾 Guardar cambios'}
          </button>
        </div>
        
        <button className="btn-panel-socio btn-accion" onClick={() => navigate('/admin')} style={{marginTop: 16, marginBottom: 8}}>
          ⬅️ Volver al Panel
        </button>
      </div>
    </div>
  );
}

export default EditarSocio; 